import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Form, InputGroup, Dropdown, Badge, Spinner } from 'react-bootstrap'
import { destinationsApi } from '../../services/api'

// Destination type from the API
interface Destination {
  type: string
  type_name: string
  type_icon: string
  id: string
  value: string
  name: string
  number: string | null
  display: string
  subtitle: string
}

// Props for the DestinationSelector component
interface DestinationSelectorProps {
  // Current value
  destinationType: string
  destinationValue: string
  // Callback when selection changes
  onChange: (type: string, value: string, display?: string) => void
  // Optional: allowed destination types (if empty, all types are allowed)
  allowedTypes?: string[]
  // Optional: exclude a specific destination (e.g., to prevent self-reference)
  excludeType?: string
  excludeId?: string
  // Optional: placeholder text
  placeholder?: string
  // Optional: whether the field is required
  required?: boolean
  // Optional: whether the field is disabled
  disabled?: boolean
  // Optional: size variant
  size?: 'sm' | 'lg'
  // Optional: show "External Number" option for manual input
  allowExternal?: boolean
  // Optional: additional class name
  className?: string
}

// Destination types with their icons (UPPERCASE keys)
const TYPE_ICONS: Record<string, string> = {
  EXTENSION: 'telephone',
  RING_GROUP: 'people',
  QUEUE: 'collection',
  IVR: 'diagram-3',
  TIME_CONDITION: 'clock',
  CONFERENCE: 'camera-video',
  VOICEMAIL: 'voicemail',
  FAX: 'printer',
  RECORDING: 'file-music',
  PARKING: 'p-square',
  NIGHT_MODE: 'moon',
  ANNOUNCEMENT: 'megaphone',
  EXTERNAL_DEST: 'telephone-outbound-fill',
  EXTERNAL: 'telephone-outbound',
  HANGUP: 'telephone-x',
  CALLBACK: 'reply',
}

const TYPE_NAMES: Record<string, string> = {
  EXTENSION: 'Nebenstelle',
  RING_GROUP: 'Rufgruppe',
  QUEUE: 'Warteschlange',
  IVR: 'IVR-Menü',
  TIME_CONDITION: 'Zeitbedingung',
  CONFERENCE: 'Konferenz',
  VOICEMAIL: 'Voicemail',
  FAX: 'Fax',
  RECORDING: 'Ansage',
  PARKING: 'Parken',
  NIGHT_MODE: 'Nachtmodus',
  ANNOUNCEMENT: 'Ansage',
  EXTERNAL_DEST: 'Externes Ziel',
  EXTERNAL: 'Externe Nummer',
  HANGUP: 'Auflegen',
  CALLBACK: 'Rückruf-Anfrage',
}

export default function DestinationSelector({
  destinationType,
  destinationValue,
  onChange,
  allowedTypes = [],
  excludeType,
  excludeId,
  placeholder = 'Ziel suchen...',
  required = false,
  disabled = false,
  size,
  allowExternal = false,
  className = '',
}: DestinationSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<Destination[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedDisplay, setSelectedDisplay] = useState('')
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [externalMode, setExternalMode] = useState(false)
  const [externalNumber, setExternalNumber] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  // Load current selection display
  useEffect(() => {
    const loadCurrentSelection = async () => {
      if (destinationType && destinationValue && destinationType !== 'HANGUP') {
        try {
          const response = await destinationsApi.resolve(destinationType, destinationValue)
          if (response.data) {
            setSelectedDisplay(response.data.display)
            if (destinationType === 'EXTERNAL') {
              setExternalNumber(destinationValue)
            }
          }
        } catch {
          // If we can't resolve, just show the value
          setSelectedDisplay(destinationValue)
        }
      } else if (destinationType === 'HANGUP') {
        setSelectedDisplay('Auflegen')
      } else {
        setSelectedDisplay('')
      }
    }
    loadCurrentSelection()
  }, [destinationType, destinationValue])

  // Search for destinations
  const searchDestinations = useCallback(async (query: string, type: string | null) => {
    setLoading(true)
    try {
      const params: Record<string, unknown> = {
        q: query || undefined,
        type: type || undefined,
        exclude_type: excludeType,
        exclude_id: excludeId,
        limit: 50,
      }

      // Filter by allowed types if specified
      if (allowedTypes.length > 0 && !type) {
        // We need to make multiple requests or filter client-side
        // For simplicity, we'll filter client-side
      }

      const response = await destinationsApi.search(params)
      let items = response.data.items || []

      // Filter by allowed types if specified (case-insensitive)
      if (allowedTypes.length > 0) {
        const allowedUpper = allowedTypes.map(t => t.toUpperCase())
        items = items.filter((item: Destination) => allowedUpper.includes(item.type.toUpperCase()))
      }

      // Add external option if allowed and searching
      if (allowExternal && query && !type) {
        const hasExternal = items.some((item: Destination) => item.type === 'EXTERNAL')
        if (!hasExternal) {
          items.push({
            type: 'EXTERNAL',
            type_name: 'Externe Nummer',
            type_icon: 'telephone-outbound',
            id: 'EXTERNAL',
            value: query,
            name: query,
            number: query,
            display: `${query} (Externe Nummer)`,
            subtitle: 'Manuelle Eingabe',
          })
        }
      }

      setResults(items)
    } catch (error) {
      console.error('Error searching destinations:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [allowedTypes, excludeType, excludeId, allowExternal])

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
    setShowDropdown(true)

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      searchDestinations(query, typeFilter)
    }, 200)
  }

  // Handle type filter click
  const handleTypeFilter = (type: string | null) => {
    setTypeFilter(type)
    searchDestinations(searchQuery, type)
  }

  // Handle selection
  const handleSelect = (dest: Destination) => {
    onChange(dest.type, dest.value, dest.display)
    setSelectedDisplay(dest.display)
    setShowDropdown(false)
    setSearchQuery('')
    setTypeFilter(null)
    setExternalMode(false)
  }

  // Handle external number input
  const handleExternalSubmit = () => {
    if (externalNumber.trim()) {
      onChange('EXTERNAL', externalNumber.trim(), externalNumber.trim())
      setSelectedDisplay(externalNumber.trim())
      setExternalMode(false)
      setShowDropdown(false)
    }
  }

  // Handle input focus
  const handleFocus = () => {
    setShowDropdown(true)
    if (results.length === 0) {
      searchDestinations('', null)
    }
  }

  // Handle click outside (check both container and portal dropdown)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current && !containerRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowDropdown(false)
        setSearchQuery('')
        setTypeFilter(null)
        setExternalMode(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (!showDropdown) return
    const updatePos = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width })
      }
    }
    updatePos()
    const handleScroll = () => updatePos()
    window.addEventListener('scroll', handleScroll, true) // capture phase for modal scroll
    window.addEventListener('resize', handleScroll)
    return () => { window.removeEventListener('scroll', handleScroll, true); window.removeEventListener('resize', handleScroll) }
  }, [showDropdown])

  // Clear selection
  const handleClear = () => {
    onChange('', '', '')
    setSelectedDisplay('')
    setSearchQuery('')
    setExternalNumber('')
    inputRef.current?.focus()
  }

  // Get available types for filter (normalize to UPPERCASE for icons/names lookup)
  const availableTypes = allowedTypes.length > 0
    ? allowedTypes.map(t => t.toUpperCase())
    : Object.keys(TYPE_NAMES)

  // Group results by type
  const groupedResults: Record<string, Destination[]> = {}
  results.forEach((dest) => {
    if (!groupedResults[dest.type]) {
      groupedResults[dest.type] = []
    }
    groupedResults[dest.type].push(dest)
  })

  return (
    <div ref={containerRef} className={`destination-selector position-relative ${className}`}>
      {/* Main input */}
      <InputGroup size={size}>
        {destinationType && (
          <InputGroup.Text className="bg-light">
            <i className={`bi bi-${TYPE_ICONS[destinationType] || 'arrow-right'}`}></i>
          </InputGroup.Text>
        )}
        <Form.Control
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={showDropdown ? searchQuery : selectedDisplay}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          disabled={disabled}
          required={required && !destinationType}
          readOnly={!showDropdown && !!selectedDisplay}
          style={selectedDisplay && !showDropdown ? { backgroundColor: '#f8f9fa', cursor: 'pointer' } : {}}
        />
        {loading && (
          <InputGroup.Text>
            <Spinner animation="border" size="sm" />
          </InputGroup.Text>
        )}
        {selectedDisplay && !disabled && (
          <InputGroup.Text
            style={{ cursor: 'pointer' }}
            onClick={handleClear}
            title="Auswahl löschen"
          >
            <i className="bi bi-x-lg text-muted"></i>
          </InputGroup.Text>
        )}
      </InputGroup>

      {/* Dropdown (Portal to body to avoid overflow clipping in modals) */}
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border rounded shadow-sm"
          style={{
            position: 'fixed',
            zIndex: 1060,
            maxHeight: '350px',
            overflowY: 'auto',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          {/* Type filter chips */}
          <div className="p-2 border-bottom d-flex flex-wrap gap-1">
            <Badge
              bg={typeFilter === null ? 'primary' : 'light'}
              text={typeFilter === null ? 'white' : 'dark'}
              style={{ cursor: 'pointer' }}
              onClick={() => handleTypeFilter(null)}
            >
              Alle
            </Badge>
            {availableTypes.filter(t => t !== 'HANGUP' && t !== 'EXTERNAL').map((type) => (
              <Badge
                key={type}
                bg={typeFilter === type ? 'primary' : 'light'}
                text={typeFilter === type ? 'white' : 'dark'}
                style={{ cursor: 'pointer' }}
                onClick={() => handleTypeFilter(type)}
              >
                <i className={`bi bi-${TYPE_ICONS[type]} me-1`}></i>
                {TYPE_NAMES[type]}
              </Badge>
            ))}
          </div>

          {/* External number input */}
          {allowExternal && externalMode && (
            <div className="p-2 border-bottom">
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="Externe Nummer eingeben..."
                  value={externalNumber}
                  onChange={(e) => setExternalNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleExternalSubmit()}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleExternalSubmit}
                  disabled={!externalNumber.trim()}
                >
                  <i className="bi bi-check-lg"></i>
                </button>
              </InputGroup>
            </div>
          )}

          {/* Results */}
          {loading ? (
            <div className="p-3 text-center text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Suche...
            </div>
          ) : (
            <div>
              {results.length === 0 && searchQuery && (
                <div className="p-3 text-center text-muted">
                  Keine Ergebnisse gefunden
                </div>
              )}
              {Object.entries(groupedResults).map(([type, dests]) => (
                <div key={type}>
                  <div className="px-3 py-1 bg-light text-muted small fw-bold">
                    <i className={`bi bi-${TYPE_ICONS[type]} me-1`}></i>
                    {TYPE_NAMES[type]}
                  </div>
                  {dests.map((dest) => (
                    <Dropdown.Item
                      key={`${dest.type}-${dest.id}`}
                      onClick={() => handleSelect(dest)}
                      className="py-2"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-medium">{dest.display}</div>
                          {dest.subtitle && (
                            <small className="text-muted">{dest.subtitle}</small>
                          )}
                        </div>
                        {dest.number && (
                          <Badge bg="secondary" className="ms-2">
                            {dest.number}
                          </Badge>
                        )}
                      </div>
                    </Dropdown.Item>
                  ))}
                </div>
              ))}

              {/* Special options section */}
              {!typeFilter && (
                <div>
                  {((!allowedTypes.length || availableTypes.includes('HANGUP') || availableTypes.includes('CALLBACK')) || allowExternal) && (
                    <div className="px-3 py-1 bg-light text-muted small fw-bold">
                      <i className="bi bi-gear me-1"></i>
                      Spezial
                    </div>
                  )}

                  {/* Callback option - show when allowed */}
                  {(!allowedTypes.length || availableTypes.includes('CALLBACK')) && (
                    <Dropdown.Item
                      onClick={() => handleSelect({
                        type: 'CALLBACK',
                        type_name: 'Rückruf-Anfrage',
                        type_icon: 'reply',
                        id: 'CALLBACK',
                        value: 'CALLBACK',
                        name: 'Rückruf-Anfrage',
                        number: null,
                        display: 'Rückruf-Anfrage',
                        subtitle: 'Per E-Mail benachrichtigen',
                      })}
                      className="py-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-reply text-success me-2"></i>
                        <div>
                          <div className="fw-medium">Rückruf-Anfrage</div>
                          <small className="text-muted">Per E-Mail benachrichtigen</small>
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  {/* Hangup option - show when allowed */}
                  {(!allowedTypes.length || availableTypes.includes('HANGUP')) && (
                    <Dropdown.Item
                      onClick={() => handleSelect({
                        type: 'HANGUP',
                        type_name: 'Auflegen',
                        type_icon: 'telephone-x',
                        id: 'HANGUP',
                        value: 'HANGUP',
                        name: 'Auflegen',
                        number: null,
                        display: 'Auflegen',
                        subtitle: 'Verbindung beenden',
                      })}
                      className="py-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-telephone-x text-danger me-2"></i>
                        <div>
                          <div className="fw-medium">Auflegen</div>
                          <small className="text-muted">Verbindung beenden</small>
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}

                  {/* External number option - show when allowExternal is true */}
                  {allowExternal && (
                    <Dropdown.Item
                      onClick={() => setExternalMode(true)}
                      className="py-2"
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-telephone-outbound text-primary me-2"></i>
                        <div>
                          <div className="fw-medium">Externe Nummer...</div>
                          <small className="text-muted">Manuelle Eingabe</small>
                        </div>
                      </div>
                    </Dropdown.Item>
                  )}
                </div>
              )}
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="hidden"
          value={destinationType || ''}
          required
        />
      )}
    </div>
  )
}
